/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import Button from "uis/Button";
import Modal from "react-modal";
import { theme } from "styles/theme";
import Icon from "uis/Icon";
import OptionButton from "uis/OptionButton";
import { useEffect, useRef, useState } from "react";

const modalStyle = {
    content: {
        top: "50%",
        left: "50%",
        right: "auto",
        bottom: "auto",
        marginRight: "-50%",
        width: "max-content",
        padding: "50px 50px 30px 50px",
        borderRadius: "20px",
        transform: "translate(-50%, -50%)",
        overflow: "visible",
    },
    overlay: {
        backgroundColor: "var(--component-color-material-dimmer)",
        zIndex: 1,
    },
};

Modal.setAppElement("#root");

type Props = {
    modalIsOpen: boolean;
    closeModal: () => void;
    onClickButton: (type: string) => void;
    onClickOption: (type: string) => void;
};

const Title = styled.p`
    font-size: 24px;
    font-weight: 700;
    text-align: center;
    margin-bottom: 20px;
`;

const Content = styled.p`
    color: ${theme.neutral900};
    font-size: 20px;
    font-weight: 400;
    text-align: center;
`;

const OptionsContainer = styled.div`
    background-color: white;
    border-radius: 12px;
    filter: drop-shadow(0px 4px 20px rgba(69, 76, 83, 0.15));

    position: absolute;
    top: 93%;
    right: 8%;
    width: fit-content;
    max-height: 200px;

    z-index: 99;
`;

const OptionItem = styled.p<{ selected: boolean }>`
    margin: 12px;
    padding: 12px;
    border-radius: 8px;
    background-color: ${(props) => (props.selected ? theme.neutral50 : "white")};
    font-weight: ${(props) => (props.selected ? 700 : 500)};
    &:hover {
        background-color: ${theme.neutral50};
    }
`;

const UpdateAlertModal = (props: Props) => {
    const { modalIsOpen, closeModal, onClickButton, onClickOption } = props;
    const [isOpenOptions, setIsOpenOptions] = useState<boolean>(false);

    const handleClick = (e: any) => {
        if (e.target.classList.contains("options-container")) {
            return;
        } else {
            if (isOpenOptions) {
                setIsOpenOptions(false);
            }
        }
    };

    useEffect(() => {
        window.addEventListener("click", handleClick);
        return () => {
            window.removeEventListener("click", handleClick);
        };
    }, [isOpenOptions]);

    return (
        <>
            <Modal
                isOpen={modalIsOpen}
                onRequestClose={() => {
                    if (isOpenOptions) {
                        setIsOpenOptions(false);
                    } else {
                        closeModal();
                    }
                }}
                style={modalStyle}
            >
                <div
                    css={css`
                        display: flex;
                        justify-content: end;
                        position: absolute;
                        right: 20px;
                        top: 20px;
                    `}
                >
                    <Icon
                        name="close"
                        onClick={() => {
                            setIsOpenOptions(false);
                            closeModal();
                        }}
                    />
                </div>
                <Title>변경 내용 알림 보내기</Title>
                <Content>방문자와 담당자들에게 변경된 내용에 대한 알림을 보내시겠습니까?</Content>
                <div
                    css={css`
                        display: flex;
                        gap: 26px;
                        margin-top: 36px;

                        justify-content: center;
                    `}
                >
                    <Button
                        onClick={() => onClickButton("cancel")}
                        title="취소하기"
                        type="destructive"
                        styles={{
                            container: css`
                                width: 150px;
                                padding: 16px 0;
                                display: flex;
                                align-items: center;
                            `,
                        }}
                    />

                    <div
                        css={css`
                            position: relative;
                        `}
                    >
                        <OptionButton
                            onClick={() => {
                                onClickButton("all-notification");
                            }}
                            onClickOption={() => {
                                setIsOpenOptions(!isOpenOptions);
                            }}
                            optionsClassName="options-container"
                            title="보내기"
                            type="primary"
                            buttonStyles={{
                                container: css`
                                    width: 110px;
                                    padding: 16px 0;
                                    display: flex;
                                    align-items: center;
                                `,
                            }}
                        />
                    </div>
                </div>
                {isOpenOptions ? (
                    <OptionsContainer>
                        <OptionItem
                            onMouseDown={(e) => {
                                e.preventDefault();
                            }}
                            selected={false}
                            onClick={() => {
                                onClickOption("visitor");
                                setIsOpenOptions(false);
                            }}
                        >
                            방문자에게만 변경 내용 알림 보내기
                        </OptionItem>
                    </OptionsContainer>
                ) : (
                    <></>
                )}
            </Modal>
        </>
    );
};

export default UpdateAlertModal;
